import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { getPhotoUrl } from "@/utils/photoUrl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, User, Calendar, Clock, MapPin, CheckCircle, Timer } from "lucide-react";

function PlayerRow({ player, status, getPhotoUrl: getPhoto }) {
  return (
    <div
      className="flex items-center gap-2.5 py-2 px-1"
      data-testid={`game-player-${player.player_id}`}
    >
      <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0 border border-border">
        {player.photo_url ? (
          <img src={getPhoto(player.photo_url)} alt={player.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </div>
      <span className="flex-1 text-sm font-medium text-foreground truncate">
        {player.name || "Jogador"}
      </span>
      {status === "confirmed" ? (
        <Badge className="bg-green-500/20 text-green-400 border-0 text-[10px] flex-shrink-0">Confirmado</Badge>
      ) : (
        <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[10px] flex-shrink-0">Espera</Badge>
      )}
    </div>
  );
}

export default function GamePlayersList({ compact = false }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/game-registrations/next/players`);
        setData(res.data);
      } catch (error) {
        console.error("Erro ao carregar inscritos:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, []);

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Carregando inscritos...
        </CardContent>
      </Card>
    );
  }

  if (!data?.game) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Nenhum jogo encontrado
        </CardContent>
      </Card>
    );
  }

  const { game, confirmed, waitlist } = data;
  const totalConfirmed = confirmed.length;
  const totalWaitlist = waitlist.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <Card className="bg-card/50 border-border/50" data-testid="game-players-list">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-foreground text-base md:text-lg">
          <Users className="h-5 w-5 text-[hsl(var(--highlight))]" />
          Inscritos - {game.title || "Próximo Jogo"}
        </CardTitle>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(game.date)}
          </span>
          {game.time && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {game.time}
            </span>
          )}
          {game.location && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
              data-testid="game-players-location-link"
            >
              <MapPin className="h-3 w-3" />
              {game.location}
            </a>
          )}
          <span className="flex items-center gap-1 font-medium">
            <Users className="h-3 w-3" />
            {totalConfirmed}/{game.max_players}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {totalConfirmed === 0 && totalWaitlist === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhum jogador inscrito ainda</p>
          </div>
        ) : (
          <div>
            {/* Confirmed */}
            {totalConfirmed > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Confirmados ({totalConfirmed})
                  </span>
                </div>
                <div className="divide-y divide-border/40">
                  {confirmed.map((p) => (
                    <PlayerRow key={p.player_id} player={p} status="confirmed" getPhotoUrl={getPhotoUrl} />
                  ))}
                </div>
              </div>
            )}

            {/* Waitlist */}
            {totalWaitlist > 0 && (
              <div className={totalConfirmed > 0 ? "mt-3 pt-3 border-t border-border/40" : ""}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Timer className="h-3.5 w-3.5 text-amber-400" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Lista de Espera ({totalWaitlist})
                  </span>
                </div>
                <div className="divide-y divide-border/40">
                  {waitlist.map((p) => (
                    <PlayerRow key={p.player_id} player={p} status="waitlist" getPhotoUrl={getPhotoUrl} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
